import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Canvas, useFrame } from "@react-three/fiber";
import { Text, OrbitControls, useTexture, Float } from "@react-three/drei";
import { XR, createXRStore } from "@react-three/xr";
import * as THREE from "three";
import { toast } from "sonner";
import { Volume2, VolumeX } from "lucide-react";
import { createForestAmbient } from "@/lib/ambient";

const CDN = "https://static.prod-images.emergentagent.com/jobs/1c220779-6edd-4bd3-b2c7-23a9d0486762/images";
const PANO_IMG = `${CDN}/30772e068c67c998a3f9d7e1635210799fdcb80e5fd48302cae8ec811f38af6c.jpeg`;

const SECTION_GUIDES = {
  Business: { key: "business", img: "/guides/rei.png", name: "REI KANSAI", role: "AI Assistant", greeting: "Welcome to the Business district. It's still under construction — ask me anything about WizLife." },
  Shopping: { key: "shopping", img: "/guides/rei.png", name: "REI KANSAI", role: "AI Assistant", greeting: "The market district isn't open yet — but I can tell you everything about WizLife. Ask away!" },
  Sports: { key: "sports", img: "/guides/rei.png", name: "REI KANSAI", role: "AI Assistant", greeting: "The Sports arena is being built. Got questions about WizLife? I'm here to help." },
};

const xrStore = createXRStore();

function PanoSphere() {
  const texture = useTexture(PANO_IMG);
  return (
    <mesh scale={[-1, 1, 1]}>
      <sphereGeometry args={[40, 48, 32]} />
      <meshBasicMaterial map={texture} side={THREE.BackSide} color="#9fd8b4" />
    </mesh>
  );
}

function GuideCharacter({ guide, onTalk }) {
  const texture = useTexture(guide.img);
  const [hover, setHover] = useState(false);
  return (
    <Float speed={1.6} rotationIntensity={0.05} floatIntensity={0.25}>
      <group position={[2.9, -1.15, -5.2]} rotation={[0, -0.3, 0]}>
        <mesh
          onClick={onTalk}
          onPointerOver={() => { setHover(true); document.body.style.cursor = "pointer"; }}
          onPointerOut={() => { setHover(false); document.body.style.cursor = "auto"; }}
        >
          <planeGeometry args={[2.15, 3.2]} />
          <meshBasicMaterial map={texture} transparent alphaTest={0.05} toneMapped={false} />
        </mesh>
        <mesh position={[0, -1.62, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.7, 0.92, 48]} />
          <meshBasicMaterial color="#2EE38A" transparent opacity={hover ? 0.9 : 0.5} />
        </mesh>
        <Text position={[0, -1.95, 0]} fontSize={0.12} color="#A8F0CE" anchorX="center">
          {guide.name} · click to talk
        </Text>
        <pointLight color="#2EE38A" intensity={4} distance={5} position={[0, 0.5, 1]} />
      </group>
    </Float>
  );
}

function SpinningRing() {
  const ref = useRef();
  useFrame((_, dt) => {
    if (ref.current) ref.current.rotation.z += dt * 0.4;
  });
  return (
    <mesh ref={ref} position={[0, 0.2, -6]}>
      <torusGeometry args={[2.1, 0.06, 12, 64]} />
      <meshStandardMaterial color="#2EE38A" emissive="#2EE38A" emissiveIntensity={1.6} />
    </mesh>
  );
}

export default function Maintenance({ section = "This district" }) {
  const nav = useNavigate();
  const guide = SECTION_GUIDES[section];
  const [soundOn, setSoundOn] = useState(false);
  const [vrSupported, setVrSupported] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const ambientRef = useRef(null);

  useEffect(() => {
    if (navigator.xr?.isSessionSupported) {
      navigator.xr.isSessionSupported("immersive-vr").then(setVrSupported).catch(() => {});
    }
    return () => ambientRef.current?.stop();
  }, []);

  const startSound = () => {
    if (!ambientRef.current) {
      ambientRef.current = createForestAmbient();
      setSoundOn(true);
    }
  };

  const toggleSound = (e) => {
    e.stopPropagation();
    if (soundOn) {
      ambientRef.current?.stop();
      ambientRef.current = null;
      setSoundOn(false);
    } else {
      startSound();
    }
  };

  const enterVR = () => {
    if (vrSupported) xrStore.enterVR();
    else toast.info("VR headset not detected — open this page in your headset's browser (Quest, Pico) to enter VR.");
  };

  return (
    <div className="relative h-[calc(100vh-4rem)]" data-testid="maintenance-page" onPointerDown={startSound}>
      <Canvas camera={{ position: [0, 0, 0.1], fov: 75 }}>
        <XR store={xrStore}>
          <ambientLight intensity={1.1} />
          <PanoSphere />
          <SpinningRing />
          {guide && <GuideCharacter guide={guide} onTalk={() => setChatOpen(true)} />}
          <Float speed={2} rotationIntensity={0.15} floatIntensity={0.5}>
            <Text position={[0, 0.6, -6]} fontSize={0.55} color="#ffffff" anchorX="center" outlineWidth={0.02} outlineColor="#0C1F14">
              UNDER MAINTENANCE
            </Text>
            <Text position={[0, -0.15, -6]} fontSize={0.2} color="#A8F0CE" anchorX="center">
              {`${section} district is being built — coming soon`}
            </Text>
          </Float>
          <OrbitControls enableZoom={false} enablePan={false} rotateSpeed={-0.4} />
        </XR>
      </Canvas>

      <div className="absolute top-4 left-4 pointer-events-none hidden sm:block" data-testid="maintenance-overlay">
        <div className="label-mono text-brand-magenta">// 360_view · drag to look around</div>
      </div>

      <div className="absolute top-4 right-4 flex gap-2">
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={toggleSound}
          className={`flex items-center gap-2 px-3 py-2 border font-mono text-xs uppercase tracking-widest transition-colors ${soundOn ? "border-brand-magenta text-brand-magenta" : "border-white/25 text-white/60 hover:text-white"}`}
          data-testid="maintenance-sound-toggle"
        >
          {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />} {soundOn ? "SOUND ON" : "SOUND OFF"}
        </button>
        <button
          onClick={enterVR}
          className="px-4 py-2 font-display font-bold text-xs uppercase tracking-widest border border-brand-cyan text-brand-cyan hover:bg-brand-cyan hover:text-black transition-colors"
          data-testid="maintenance-enter-vr-button"
        >
          ◉ ENTER VR
        </button>
      </div>

      <div className="absolute bottom-6 left-4">
        {guide && !chatOpen && (
          <button
            onClick={() => setChatOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-black/50 backdrop-blur border border-brand-magenta/60 text-brand-magenta font-display font-bold text-xs uppercase tracking-widest hover:bg-brand-magenta hover:text-ink-900 transition-colors"
            data-testid="talk-to-guide-button"
          >
            <span className="dot-live" /> TALK TO {guide.name.split(" ")[0]}
          </button>
        )}
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
        <button onClick={() => nav("/")} className="btn-neon !px-6 !py-3 text-xs" data-testid="maintenance-back-button">
          ← BACK TO ECOSYSTEM
        </button>
      </div>

      {guide && chatOpen && <GuideChat guide={guide} onClose={() => setChatOpen(false)} />}
    </div>
  );
}

function GuideChat({ guide, onClose }) {
  const [messages, setMessages] = useState([{ role: "assistant", text: guide.greeting }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      let sid = localStorage.getItem("wiz_assistant_sid");
      if (!sid) {
        sid = `s_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`;
        localStorage.setItem("wiz_assistant_sid", sid);
      }
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ section: guide.key, session_id: sid, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "…the grid glitched. Try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="absolute bottom-6 right-4 w-[340px] max-w-[calc(100vw-2rem)] panel flex flex-col" style={{ height: "420px" }} data-testid="guide-chat-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="dot-live" />
          <div>
            <div className="font-display font-bold text-sm leading-none">{guide.name}</div>
            <div className="label-mono text-brand-magenta mt-0.5">WizLife AI · online</div>
          </div>
        </div>
        <button onClick={onClose} className="text-white/50 hover:text-white text-lg leading-none px-1" data-testid="guide-chat-close">✕</button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
        {messages.map((m, i) => (
          <div key={i} className={`max-w-[85%] px-3 py-2 text-sm font-body ${m.role === "user" ? "ml-auto bg-brand-magenta/20 border border-brand-magenta/40" : "bg-black/40 border border-white/10"}`} data-testid={`guide-msg-${m.role}-${i}`}>
            {m.text || <span className="text-white/40 font-mono text-xs">typing…</span>}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="p-3 border-t border-white/10 flex gap-2">
        <input
          className="field flex-1 !py-2 text-sm"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder={`Ask ${guide.name.split(" ")[0]}…`}
          data-testid="guide-chat-input"
        />
        <button onClick={send} disabled={busy} className="btn-neon !py-2 !px-4 text-xs" data-testid="guide-chat-send">→</button>
      </div>
    </div>
  );
}
